/**
 * Curtain Management Component
 * Allows trainers to upload, edit, and delete curtain images for smartview chatbots
 * Only visible when collection instructions include 'smartview'
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Typography,
  TextField,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { Curtain } from '../types';
import {
  getCurtainsForCollection,
  createCurtain,
  updateCurtain,
  deleteCurtain,
  resetCurtainCollection
} from '../services/api';

interface CurtainManagementProps {
  aiId: string;
  collectionId: string;
}

export const CurtainManagement: React.FC<CurtainManagementProps> = ({ aiId, collectionId }) => {
  const [curtains, setCurtains] = useState<Curtain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Reset states
  const [needsMigration, setNeedsMigration] = useState(false);
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);
  const [migrationSQL, setMigrationSQL] = useState('');
  const [migrationInstructions, setMigrationInstructions] = useState<string[]>([]);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Form states
  const [selectedCurtain, setSelectedCurtain] = useState<Curtain | null>(null);
  const [newIdentifier, setNewIdentifier] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newImageData, setNewImageData] = useState('');
  const [uploading, setUploading] = useState(false);

  // Load curtains on mount
  useEffect(() => {
    loadCurtains();
  }, [aiId, collectionId]);

  const loadCurtains = async () => {
    try {
      setLoading(true);
      setError('');
      setNeedsMigration(false);
      const response = await getCurtainsForCollection(aiId, collectionId);
      setCurtains(response.data.curtains || []);
    } catch (err: any) {
      console.error('Error loading curtains:', err);

      // Check if backend flagged this as needing migration
      if (err.response?.data?.needsMigration) {
        setNeedsMigration(true);
        setError('Curtain reset required. Click "Reset Curtains" to clear old curtains and prepare a fresh collection.');
      } else {
        const errorMessage = err.response?.data?.error ||
                            err.response?.data?.message ||
                            err.message ||
                            'Failed to load curtains';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file must be less than 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('File must be an image');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      setNewImageData(base64);
      setError('');
    };
    reader.onerror = () => {
      setError('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const handleAddCurtain = async () => {
    if (!newIdentifier || !newImageData) {
      setError('Identifier and image are required');
      return;
    }

    try {
      setUploading(true);
      setError('');
      await createCurtain(aiId, collectionId, {
        identifier: newIdentifier,
        image_data: newImageData,
        description: newDescription || `Curtain ${newIdentifier}`
      });
      setSuccess(`Successfully added curtain "${newIdentifier}"`);
      setAddDialogOpen(false);
      setNewIdentifier('');
      setNewDescription('');
      setNewImageData('');
      await loadCurtains();
    } catch (err: any) {
      console.error('Error adding curtain:', err);
      setError(err.response?.data?.error || 'Failed to add curtain');
    } finally {
      setUploading(false);
    }
  };

  const handleEditCurtain = async () => {
    if (!selectedCurtain) return;

    try {
      setUploading(true);
      setError('');
      const updateData: any = {};
      if (newIdentifier && newIdentifier !== selectedCurtain.identifier) {
        updateData.identifier = newIdentifier;
      }
      if (newDescription && newDescription !== selectedCurtain.description) {
        updateData.description = newDescription;
      }
      if (newImageData && newImageData !== selectedCurtain.image_data) {
        updateData.image_data = newImageData;
      }

      await updateCurtain(aiId, collectionId, selectedCurtain.id, updateData);
      setSuccess(`Successfully updated curtain "${selectedCurtain.identifier}"`);
      setEditDialogOpen(false);
      setSelectedCurtain(null);
      setNewIdentifier('');
      setNewDescription('');
      setNewImageData('');
      await loadCurtains();
    } catch (err: any) {
      console.error('Error updating curtain:', err);
      setError(err.response?.data?.error || 'Failed to update curtain');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteCurtain = async () => {
    if (!selectedCurtain) return;

    try {
      setUploading(true);
      setError('');
      await deleteCurtain(aiId, collectionId, selectedCurtain.id);
      setSuccess(`Successfully deleted curtain "${selectedCurtain.identifier}"`);
      setDeleteDialogOpen(false);
      setSelectedCurtain(null);
      await loadCurtains();
    } catch (err: any) {
      console.error('Error deleting curtain:', err);
      setError(err.response?.data?.error || 'Failed to delete curtain');
    } finally {
      setUploading(false);
    }
  };

  const handleResetCurtains = async () => {
    try {
      setUploading(true);
      setError('');
      const response = await resetCurtainCollection(aiId, collectionId);

      // Check if reset completed automatically
      if (response.data.migrationComplete) {
        setSuccess(response.data.message || 'Curtain reset completed successfully!');
        setNeedsMigration(false);
        // Reload curtains to verify reset worked
        await loadCurtains();
      } else {
        // Automatic reset partially failed, show manual SQL
        setMigrationSQL(response.data.sql || '');
        setMigrationInstructions(response.data.instructions || []);
        setMigrationDialogOpen(true);
        setNeedsMigration(false);
        setSuccess(response.data.message || 'Please run the SQL commands shown in the dialog.');
      }
    } catch (err: any) {
      console.error('Error resetting curtains:', err);
      setError(err.response?.data?.error || 'Failed to reset curtains');
    } finally {
      setUploading(false);
    }
  };

  const openEditDialog = (curtain: Curtain) => {
    setSelectedCurtain(curtain);
    setNewIdentifier(curtain.identifier);
    setNewDescription(curtain.description);
    setNewImageData(curtain.image_data);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (curtain: Curtain) => {
    setSelectedCurtain(curtain);
    setDeleteDialogOpen(true);
  };

  const openAddDialog = () => {
    setNewIdentifier('');
    setNewDescription('');
    setNewImageData('');
    setAddDialogOpen(true);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Curtain Images</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddPhotoAlternateIcon />}
          onClick={openAddDialog}
        >
          Add Curtain
        </Button>
      </Box>

      {error && (
        <Alert
          severity={needsMigration ? 'warning' : 'error'}
          sx={{ mb: 2 }}
          onClose={() => setError('')}
          action={
            needsMigration ? (
              <Button
                color="inherit"
                size="small"
                onClick={handleResetCurtains}
                disabled={uploading}
              >
                {uploading ? <CircularProgress size={20} /> : 'Reset Curtains'}
              </Button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : curtains.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body1" color="textSecondary">
            No curtains uploaded yet. Click "Add Curtain" to get started.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {curtains.map((curtain) => (
            <Grid item xs={12} sm={6} md={4} key={curtain.id}>
              <Card>
                <CardMedia
                  component="img"
                  height="200"
                  image={curtain.image_data}
                  alt={curtain.identifier}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {curtain.identifier}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {curtain.description}
                  </Typography>
                </CardContent>
                <CardActions>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => openEditDialog(curtain)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => openDeleteDialog(curtain)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Curtain Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Curtain</DialogTitle>
        <DialogContent>
          <TextField
            label="Identifier"
            value={newIdentifier}
            onChange={(e) => setNewIdentifier(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., blue_sheer"
            helperText="A unique label for this curtain (e.g., blue_sheer, red_velvet, white_linen)"
          />
          <TextField
            label="Description"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., Blue striped curtain"
            helperText="Optional description of the curtain"
          />
          <Box sx={{ mt: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="add-curtain-image"
              type="file"
              onChange={handleImageUpload}
            />
            <label htmlFor="add-curtain-image">
              <Button variant="outlined" component="span" fullWidth>
                Upload Image (Max 5MB)
              </Button>
            </label>
          </Box>
          {newImageData && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <img
                src={newImageData}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddCurtain}
            variant="contained"
            color="primary"
            disabled={uploading || !newIdentifier || !newImageData}
          >
            {uploading ? <CircularProgress size={24} /> : 'Add Curtain'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Curtain Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Curtain</DialogTitle>
        <DialogContent>
          <TextField
            label="Identifier"
            value={newIdentifier}
            onChange={(e) => setNewIdentifier(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., blue_sheer"
          />
          <TextField
            label="Description"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., Blue striped curtain"
          />
          <Box sx={{ mt: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="edit-curtain-image"
              type="file"
              onChange={handleImageUpload}
            />
            <label htmlFor="edit-curtain-image">
              <Button variant="outlined" component="span" fullWidth>
                Replace Image (Max 5MB)
              </Button>
            </label>
          </Box>
          {newImageData && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <img
                src={newImageData}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleEditCurtain}
            variant="contained"
            color="primary"
            disabled={uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Curtain Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Curtain</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete curtain "{selectedCurtain?.identifier}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCurtain}
            variant="contained"
            color="error"
            disabled={uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual SQL Instructions Dialog */}
      <Dialog open={migrationDialogOpen} onClose={() => setMigrationDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Manual SQL Required</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Automatic curtain reset could not finish. Follow these steps to complete it:
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Instructions:
          </Typography>
          {migrationInstructions.map((instruction, index) => (
            <Typography key={index} variant="body2" sx={{ ml: 2, mb: 1 }}>
              {instruction}
            </Typography>
          ))}

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            SQL Commands:
          </Typography>
          <TextField
            multiline
            fullWidth
            rows={10}
            value={migrationSQL}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(migrationSQL);
              setSuccess('SQL copied to clipboard!');
            }}
            variant="outlined"
          >
            Copy SQL
          </Button>
          <Button onClick={() => setMigrationDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CurtainManagement;
